import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'
import * as yup from 'yup'
import moment from 'moment'
import { times } from 'lodash'
import { faArrowLeft, faPenToSquare } from '@fortawesome/pro-regular-svg-icons'
import { faSuitcase } from '@fortawesome/pro-solid-svg-icons'
import {
  Alert,
  Breadcrumb,
  Card,
  Heading,
  HeadingVariants,
  Icon,
  Text,
  TooltipProvider,
  Form,
} from '@/ui'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import { UPDATE_USER_KEY, updateUser } from '@/actions/userActions'
import { selectErrorsForKeys } from '@/reducers/fetch'
import {
  getCurrentUser,
  getFinancialProfile,
  selectIsCurrentUserScorp,
} from '@/selectors/user.selectors'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'

import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { PostOnboardingTaskIdents } from '@/features/onboarding/config'
import { BUSINESS_ENTITY_OPTIONS } from '@/constants/onboardingConstants'
import { STATES } from '@/constants/locationConstants'

import { fetchUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'

import { TextColors, TypographyStyles } from '@/styles'
import { PracticeProfileFormCta } from '../PracticeProfileFormCta'
import { DocumentUploads } from './DocumentUploads'

// TODO: Migrate to V2
import ChangeEmailModal from '@/features/user/your-practice/profile/your-business/ChangeEmailModal'
import { LabelDescription } from '@/componentsV1/BaseComponents/Input'
import {
  FormikLocationSearchInput,
  getFieldNames,
  makeDateSchema,
  makeEinSchema,
  makeReqPhoneNumberSchema,
  makeReqStringSchema,
} from '@/base'

export const YourBusiness = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const user = useReselector(getCurrentUser)
  const fp = useReselector(getFinancialProfile)
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const [changeEmailModalOpen, setChangeEmailModalOpen] = useState(false)
  const errors = useReselector(selectErrorsForKeys, [
    UPDATE_USER_KEY,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id),
  ])

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      businessName: fp?.businessName ? fp?.businessName : undefined,
      businessEntity: fp?.businessEntity ? fp?.businessEntity : undefined,
      einNumber: fp?.einNumber ? fp?.einNumber : undefined,
      yearPracticeStarted: fp?.yearPracticeStarted
        ? fp?.yearPracticeStarted.toString()
        : undefined,
      businessAddress: fp?.businessAddress ? fp?.businessAddress : undefined,
      homeState: fp?.homeState ? fp?.homeState : undefined,
      scorpRegistrationState: fp?.scorpRegistrationState
        ? fp?.scorpRegistrationState
        : undefined,
      entityChangeElectionDate:
        fp?.entityChangeElectionDate &&
        DateTime.fromISO(fp.entityChangeElectionDate, { zone: 'utc' }).toFormat(
          DATE_FORMATS_LUXON.INPUT
        ),
      phoneNumber: user?.phoneNumber ? user?.phoneNumber : undefined,
    },

    onSubmit: async ({
      businessName,
      businessEntity,
      einNumber,
      yearPracticeStarted,
      businessAddress,
      homeState,
      scorpRegistrationState,
      entityChangeElectionDate,
      phoneNumber,
    }) => {
      const fpUpdateRes = await dispatch(
        updateFinancialProfile(fp?.id, {
          businessName: businessName || undefined,
          businessEntity: businessEntity || undefined,
          einNumber: einNumber || undefined,
          yearPracticeStarted: yearPracticeStarted || undefined,
          businessAddress: businessAddress || undefined,
          homeState: homeState || undefined,
          scorpRegistrationState: scorpRegistrationState || undefined,
          entityChangeElectionDate: entityChangeElectionDate || undefined,
        })
      )

      const userUpdateRes = await dispatch(
        updateUser(user?.id, { phoneNumber })
      )

      await markUserActionItemCompleteIfExists(
        PostOnboardingTaskIdents.FILL_OUT_BUSINESS_PROFILE,
        (event, properties) => track(event, properties)
      )

      if (fpUpdateRes && userUpdateRes) {
        navigate('/practice/profile')
      }
    },
  })

  const { submitForm, isSubmitting } = formik
  const fieldNames = getFieldNames(formik)

  // TODO: Fix tooltip and file upload
  return (
    <div className="flex flex-col gap-2 px-4">
      <Breadcrumb>
        <Breadcrumb.Item>
          <Breadcrumb.Link
            onClick={() => {
              navigate('/practice/profile')
            }}
          >
            <Icon icon={faArrowLeft} style={{ marginRight: 8 }} />
            Back to Practice Profile
          </Breadcrumb.Link>
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="flex flex-row gap-3 items-center">
        <Icon
          icon={faSuitcase}
          backgroundStyle="rounded"
          backgroundColor="priority2"
          size="sm"
        />
        <Heading variant={HeadingVariants.H2}>Your Business</Heading>
      </div>

      <Card>
        <TooltipProvider>
          <FormikProvider value={formik}>
            <div className="flex flex-col gap-3">
              {Boolean(errors?.length) && (
                <Alert variant="error">{errors[0].message}</Alert>
              )}

              <Form.Input
                name={fieldNames.businessName}
                label="Business Name"
                placeholder="Enter Business Name"
                schema={makeReqStringSchema({
                  field: 'business name',
                  required: false,
                })}
              />

              <Form.Select
                name={fieldNames.businessEntity}
                label="Business Entity"
                placeholder="Select Business Entity"
                options={BUSINESS_ENTITY_OPTIONS}
                schema={yup.string().nullable().optional()}
              />

              <Form.Input
                name={fieldNames.einNumber}
                label="Business EIN Number"
                placeholder="##-#######"
                format="##-#######"
                componentType="pattern"
                schema={makeEinSchema({ required: false })}
              />

              <Form.Select
                label="Year Practice Started"
                placeholder="Select Year"
                name={fieldNames.yearPracticeStarted}
                // Gets a list of years between now and 10 years ago
                options={times(10).map((value) => {
                  const year = (moment().year() - value).toString()
                  return {
                    value: year,
                    text: year,
                  }
                })}
                schema={makeReqStringSchema({
                  field: 'year practice started',
                  required: false,
                })}
              />

              <FormikLocationSearchInput
                label="Business Address"
                placeholder="Street address, city, state, zip code"
                name={fieldNames.businessAddress}
                schema={makeReqStringSchema({
                  field: 'business address',
                  required: false,
                })}
              />

              <Form.Select
                label="Home State"
                placeholder="Select state"
                name={fieldNames.homeState}
                options={STATES}
                schema={makeReqStringSchema({
                  field: 'home state',
                  required: false,
                })}
              />

              {isScorp && (
                <>
                  <Form.Select
                    label="Business Registration State"
                    placeholder="Select state"
                    name={fieldNames.scorpRegistrationState}
                    options={STATES}
                    schema={makeReqStringSchema({
                      field: 'business registration state',
                      required: false,
                    })}
                  />
                  <Form.DateInput
                    label="S Corp Election Date"
                    name={fieldNames.entityChangeElectionDate}
                    schema={makeDateSchema()}
                    placeholder="Select Date"
                  />
                </>
              )}

              <Form.Input
                name={fieldNames.phoneNumber}
                label="Business Phone"
                componentType="phone"
                schema={makeReqPhoneNumberSchema()}
              />

              <div className="w-full">
                <LabelDescription label="Business Email" />

                <div className="flex items-center">
                  <Text
                    variant={TypographyStyles.BODY_LG}
                    color={TextColors.MUTED}
                  >
                    {user?.email}
                  </Text>

                  <Icon
                    icon={faPenToSquare}
                    onClick={() => setChangeEmailModalOpen(true)}
                    className="ml-16"
                  />
                </div>
              </div>

              <DocumentUploads
                businessEntity={fp?.businessEntity ?? undefined}
                isScorp={isScorp}
              />

              <PracticeProfileFormCta
                submitForm={submitForm}
                isValid
                isSubmitting={isSubmitting}
              />
            </div>
          </FormikProvider>
        </TooltipProvider>
      </Card>

      <ChangeEmailModal
        open={changeEmailModalOpen}
        close={() => setChangeEmailModalOpen(false)}
      />
    </div>
  )
}
